import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CurrencyInput } from "@/components/app/CurrencyInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { SptTable, TotalRow, Amount } from "@/components/spt/SptTabs";
import { CopyValue } from "@/components/spt/CopyValue";

const JENIS = ["PPh 21", "PPh 22", "PPh 23", "PPh 4(2)", "PPh 26"];
const blank = (tahun) => ({ tahun, nomor: "", tanggal: "", jenis_pajak: "PPh 21", npwp_pemotong: "", nama_pemotong: "", kode_objek: "", penghasilan_bruto: 0, pph_dipotong: 0 });

function BupotForm({ form, setForm }) {
  const upd = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div className="space-y-1.5 sm:col-span-2"><Label>Nomor Bukti Potong</Label><Input value={form.nomor} onChange={upd("nomor")} data-testid="bupot-nomor"/></div>
      <div className="space-y-1.5"><Label>Tanggal</Label><Input type="date" value={form.tanggal} onChange={upd("tanggal")} data-testid="bupot-tanggal"/></div>
      <div className="space-y-1.5"><Label>Jenis Pajak</Label>
        <Select value={form.jenis_pajak} onValueChange={upd("jenis_pajak")}><SelectTrigger data-testid="bupot-jenis"><SelectValue/></SelectTrigger>
          <SelectContent>{JENIS.map((j) => <SelectItem key={j} value={j}>{j}</SelectItem>)}</SelectContent></Select></div>
      <div className="space-y-1.5"><Label>NPWP Pemotong</Label><Input value={form.npwp_pemotong} onChange={upd("npwp_pemotong")} data-testid="bupot-npwp"/></div>
      <div className="space-y-1.5"><Label>Nama Pemotong</Label><Input value={form.nama_pemotong} onChange={upd("nama_pemotong")} data-testid="bupot-nama"/></div>
      <div className="space-y-1.5"><Label>Kode Objek Pajak</Label><Input value={form.kode_objek} onChange={upd("kode_objek")} placeholder="mis. 21-100-01" data-testid="bupot-kode"/></div>
      <div className="space-y-1.5"><Label>Penghasilan Bruto</Label><CurrencyInput value={form.penghasilan_bruto} onValueChange={upd("penghasilan_bruto")} data-testid="bupot-bruto"/></div>
      <div className="space-y-1.5"><Label>PPh Dipotong</Label><CurrencyInput value={form.pph_dipotong} onValueChange={upd("pph_dipotong")} data-testid="bupot-pph"/></div>
    </div>
  );
}

export function TabL1E({ d, tahun, reload }) {
  const [form, setForm] = useState(null);
  const save = async () => {
    try {
      const body = { ...form, tahun };
      form.id ? await api.put(`/bukti-potong/${form.id}`, body) : await api.post("/bukti-potong", body);
      toast.success("Bukti potong disimpan"); setForm(null); reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (b) => {
    if (!window.confirm(`Hapus bukti potong ${b.nomor}?`)) return;
    try { await api.delete(`/bukti-potong/${b.id}`); reload(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-3">
      <div className="flex justify-end"><Button size="sm" onClick={() => setForm(blank(tahun))} data-testid="bupot-add-btn"><Plus size={14}/> Tambah Bukti Potong</Button></div>
      <SptTable testid="spt-l1e-table" head={["Nomor", "Pemotong", "Jenis", "Bruto", "PPh Dipotong"]} empty="Belum ada bukti potong. Tambahkan dari e-Bupot."
        rows={d.rows.map((b) => (
          <tr key={b.id} data-testid={`bupot-row-${b.id}`}>
            <td className="p-2.5 border-t border-slate-100"><div className="flex items-center gap-2"><span className="font-mono text-[12px]">{b.nomor}</span><CopyValue raw value={b.nomor} label="Nomor" testid={`bupot-copy-nomor-${b.id}`}/></div></td>
            <td className="p-2.5 border-t border-slate-100 text-[13px]">{b.nama_pemotong}<div className="text-[11px] text-slate-500">{b.npwp_pemotong}</div></td>
            <td className="p-2.5 border-t border-slate-100">{b.jenis_pajak}</td>
            <td className="p-2.5 border-t border-slate-100 text-right"><Amount v={b.penghasilan_bruto} testid={`bupot-copy-bruto-${b.id}`} label="Bruto"/></td>
            <td className="p-2.5 border-t border-slate-100 text-right"><div className="flex items-center justify-end gap-1"><Amount v={b.pph_dipotong} testid={`bupot-copy-pph-${b.id}`} label="PPh"/>
              <button onClick={() => setForm({ ...b })} className="p-1.5 hover:bg-slate-100 rounded-md" data-testid={`bupot-edit-${b.id}`}><Pencil size={13}/></button>
              <button onClick={() => del(b)} className="p-1.5 hover:bg-rose-50 rounded-md" data-testid={`bupot-del-${b.id}`}><Trash2 size={13} className="text-rose-600"/></button></div></td>
          </tr>))}/>
      <TotalRow label="Total Penghasilan Bruto" v={d.total_bruto} testid="spt-l1e-total-bruto"/>
      <TotalRow label="Total PPh Dipotong" v={d.total_pph} testid="spt-l1e-total-pph"/>
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-lg" data-testid="bupot-dialog">
          <DialogHeader><DialogTitle>{form?.id ? "Ubah" : "Tambah"} Bukti Potong</DialogTitle></DialogHeader>
          {form && <BupotForm form={form} setForm={setForm}/>}
          <DialogFooter data-dialog-footer><Button variant="outline" onClick={() => setForm(null)}>Batal</Button><Button onClick={save} data-testid="bupot-save-btn">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
